import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {ChevronDownIcon} from 'lucide-react';
import {cn} from '@chekinapp/ui';

type FloatingPillProps = ComponentPropsWithoutRef<'button'> & {
  visible: boolean;
  icon?: ReactNode;
  containerClassName?: string;
  containerStyle?: CSSProperties;
};

const FloatingPill = forwardRef<HTMLButtonElement, FloatingPillProps>(
  (
    {
      visible,
      icon,
      containerClassName,
      containerStyle,
      className,
      children,
      type = 'button',
      ...props
    },
    ref,
  ) => (
    <div
      data-visible={visible}
      aria-hidden={!visible || undefined}
      style={containerStyle}
      className={cn(
        'pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4',
        'bottom-[calc(18px+env(safe-area-inset-bottom))]',
        'transition-opacity duration-200 motion-reduce:transition-none',
        visible ? 'opacity-100' : 'opacity-0',
        '[body[data-scroll-locked]_&]:hidden',
        containerClassName,
      )}
    >
      <button
        ref={ref}
        type={type}
        tabIndex={visible ? undefined : -1}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-[var(--button-radius)] py-0 pl-3 pr-4',
          'min-h-[40px] text-[13px] font-semibold',
          'bg-[var(--chekin-color-brand-navy)] text-[color:var(--chekin-color-white)] [box-shadow:0_6px_18px_color-mix(in_srgb,var(--chekin-color-brand-navy)_30%,transparent)]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--chekin-color-brand-blue)]',
          '[&_svg]:size-5 [&_svg]:shrink-0',
          visible && 'pointer-events-auto',
          className,
        )}
        {...props}
      >
        {icon ?? <ChevronDownIcon aria-hidden="true" strokeWidth={2.2} />}
        {children}
      </button>
    </div>
  ),
);
FloatingPill.displayName = 'FloatingPill';

export {FloatingPill};
export type {FloatingPillProps};
